import { errorHelpers, hooks } from '@approvalmax/utils';
import { useCallback, useMemo } from 'react';

import { useItemsGroupedByWallet } from './useItemsGroupedByWallet/useItemsGroupedByWallet';
import { useRequestWalletsBalances } from './useRequestWalletsBalances/useRequestWalletsBalances';

export const useWalletsByLineItems = (
    companyId: string,
    requestId: string,
    items: ReturnType<typeof useItemsGroupedByWallet>['groupedByWallet'],
    needBalance = true
) => {
    const filterWalletsUsedByBatchPayment = useCallback<
        NonNullable<Parameters<typeof useRequestWalletsBalances>[0]['walletFilter']>
    >(
        (wallet) => {
            return items.some((groupedByWallet) => groupedByWallet.currency === wallet.currency);
        },
        [items]
    );

    const {
        walletsWithBalances: unsortedWallets,
        isLoading: isLoadingWallets,
        refetchWalletsBalances,
    } = useRequestWalletsBalances({
        companyId,
        requestId,
        needBalance,
        walletFilter: filterWalletsUsedByBatchPayment,
    });

    const walletsMap = useMemo(
        () => new Map((unsortedWallets || []).map((wallet) => [wallet.id, wallet])),
        [unsortedWallets]
    );

    const sortedWallets = useMemo(
        () =>
            walletsMap.size
                ? items.map((item) => {
                      const wallet = walletsMap.get(item.currency);

                      if (!wallet) {
                          throw errorHelpers.notFoundError();
                      }

                      return wallet;
                  })
                : [],
        [items, walletsMap]
    );

    const handleRefreshBalances = hooks.useDelayedCallback(() => {
        void refetchWalletsBalances();
    }, 1000);

    return { wallets: sortedWallets, isLoadingWallets, onRefreshBalances: handleRefreshBalances };
};
